/**
 * Workflow template placeholder syntax (UI)
 *
 * The editor standardizes on curly braces for template variables. Older templates may still
 * contain legacy square-bracket template placeholders; those are flagged as deprecated (except
 * known file-rename tokens below).
 *
 * - Template variables: {variableName} (same naming rules as Settings tab)
 * - File rename / date tokens: [TOKEN] — literal, not validated as template variables
 * - Legacy (deprecated): [variableName] where the inner name looks like a template variable
 *   and is not a known rename token
 */

import type { WorkflowTemplateVariable } from "../types/WorkflowTemplates/WorkflowTemplateVariable";
import {
  BUCKET_ROLE_CACHE,
  BUCKET_ROLE_FILE_PICKUP,
  BUCKET_ROLE_HOME,
} from "./bucket-roles";
import { GlobalVariables } from "./constants";

/** Inner token match for {variableName} */
const TEMPLATE_VAR_INNER = "[a-zA-Z0-9][a-zA-Z0-9_\\-]*";

/** Global: all {variableName} placeholders */
const TEMPLATE_PLACEHOLDER_G = new RegExp(`\\{(${TEMPLATE_VAR_INNER})\\}`, "g");

/** Single-segment test: entire string is one placeholder */
const TEMPLATE_PLACEHOLDER_FULL = new RegExp(`^\\{(${TEMPLATE_VAR_INNER})\\}$`);

/**
 * Square-bracket tokens allowed in field values (file rename patterns, paths, etc.).
 * These are expanded by the Workflow API at processing time—not template variables from Settings.
 * Compared case-insensitively via isKnownFileRenameToken.
 */
const KNOWN_FILE_RENAME_TOKENS_UPPER = new Set<string>([
  // Workflow API predefined (UTC processing time, environment, trace, filename parts)
  "DD",
  "ENV",
  "EPOCH_TIME",
  "HH",
  "MM",
  "ORIGINAL",
  "ORIGINAL_NOEXT",
  "SS",
  "TRACE_ID",
  "TT",
  "YYYY",
  "YY",
  // Additional tokens used in patterns / older docs (still not deprecated template syntax)
  "YYYYMM",
  "MMMM",
  "M",
  "D",
  "H",
  "S",
  "EXT",
  "NAME",
  "FILENAME",
  "TIMESTAMP",
  "TS",
  "DATE",
  "TIME",
  "YEAR",
  "MONTH",
  "DAY",
  "WEEK",
  "SEQ",
  "SEQUENCE",
  "GUID",
  "UUID",
]);

export function isKnownFileRenameToken(tokenInner: string): boolean {
  return KNOWN_FILE_RENAME_TOKENS_UPPER.has(tokenInner.trim().toUpperCase());
}

/** True if inner matches template variable name rules (Settings tab) */
export function isTemplateVariableInnerName(inner: string): boolean {
  return /^[a-zA-Z0-9][a-zA-Z0-9_\-]*$/.test(inner);
}

/** Infrastructure bucket tokens persisted in paths — not user-defined template variables (ACME-1888). */
const INFRASTRUCTURE_BUCKET_TOKENS = [
  BUCKET_ROLE_HOME,
  BUCKET_ROLE_CACHE,
  BUCKET_ROLE_FILE_PICKUP,
] as const;

function escapeRegExpLiteral(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function maskInfrastructureBucketTokens(value: string): string {
  let masked = value;
  for (const token of INFRASTRUCTURE_BUCKET_TOKENS) {
    masked = masked.replace(new RegExp(escapeRegExpLiteral(token), "gi"), "");
  }
  return masked;
}

/**
 * Extract unique template variable names from {placeholders} in order of first appearance.
 * Skips inner braces inside infrastructure tokens such as {{home}} so {home} is not treated as a variable.
 */
export function extractTemplateVariableNames(value: string | null | undefined): string[] {
  if (!value) return [];
  const variables: string[] = [];
  const regex = new RegExp(TEMPLATE_PLACEHOLDER_G.source, "g");
  const masked = maskInfrastructureBucketTokens(value);
  let match: RegExpExecArray | null;
  while ((match = regex.exec(masked)) !== null) {
    if (!variables.includes(match[1])) {
      variables.push(match[1]);
    }
  }
  return variables;
}

function pushTemplateFieldString(out: string[], value: unknown): void {
  if (typeof value === "string" && value) {
    out.push(value);
  }
}

function pushTemplateFieldMapValues(out: string[], raw: unknown): void {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return;
  }
  for (const value of Object.values(raw as Record<string, unknown>)) {
    pushTemplateFieldString(out, value);
  }
}

function pushFileLegTemplateFields(out: string[], leg: unknown): void {
  if (!leg || typeof leg !== "object") {
    return;
  }
  const info = leg as Record<string, unknown>;
  pushTemplateFieldString(out, info.path ?? info.Path);
  pushTemplateFieldString(out, info.name ?? info.Name);
  pushTemplateFieldString(out, info.bucketRename ?? info.BucketRename);
  pushTemplateFieldString(out, info.locationType ?? info.LocationType);
  pushTemplateFieldString(out, info.storageType ?? info.StorageType);
  pushTemplateFieldString(out, info.hostName ?? info.HostName);
  pushTemplateFieldString(out, info.region ?? info.Region);
  pushTemplateFieldMapValues(out, info.fileRename ?? info.FileRename);
  pushTemplateFieldMapValues(
    out,
    info.credentialPlaceholders ??
    info.CredentialPlaceholders ??
    info.secretValue ??
    info.SecretValue
  );
}

/**
 * Every persisted step string that can hold a user-defined {variable}.
 * Save and the Settings trash can both use this so a name is used or unused
 * the same way no matter which step field it sits in.
 * Regex rules and variable descriptions are not fields.
 */
export function collectStepTemplateFieldStrings(step: unknown): string[] {
  const out: string[] = [];
  if (!step || typeof step !== "object") {
    return out;
  }
  const s = step as Record<string, unknown>;
  pushTemplateFieldString(out, s.stepName ?? s.StepName);
  pushTemplateFieldString(out, s.locationType ?? s.LocationType);
  pushTemplateFieldString(out, s.storageType ?? s.StorageType);
  pushTemplateFieldString(out, s.path ?? s.Path);
  pushTemplateFieldString(out, s.name ?? s.Name);
  pushTemplateFieldString(out, s.bucketRename ?? s.BucketRename);
  pushTemplateFieldString(out, s.hostName ?? s.HostName);
  pushTemplateFieldString(out, s.region ?? s.Region);
  pushTemplateFieldString(out, s.port ?? s.Port);
  pushTemplateFieldString(out, s.userName ?? s.UserName);
  pushTemplateFieldString(out, s.password ?? s.Password);
  pushTemplateFieldString(out, s.connectionString ?? s.ConnectionString);
  pushTemplateFieldString(out, s.pgpKey ?? s.PGPKey);
  pushTemplateFieldString(out, s.roleARN ?? s.RoleARN);
  pushTemplateFieldMapValues(out, s.fileRename ?? s.FileRename);
  pushTemplateFieldMapValues(
    out,
    s.credentialPlaceholders ?? s.CredentialPlaceholders ?? s.secretValue ?? s.SecretValue
  );

  const fileMovePattern = s.fileMovePattern ?? s.FileMovePattern;
  if (Array.isArray(fileMovePattern)) {
    for (const item of fileMovePattern) {
      pushTemplateFieldString(out, item);
    }
  }

  pushFileLegTemplateFields(out, s.destinationFileInfo ?? s.DestinationFileInfo);
  pushFileLegTemplateFields(out, s.sourceFileInfo ?? s.SourceFileInfo);

  const config = (s.stepConfiguration ?? s.StepConfiguration) as Record<string, unknown> | undefined;
  if (config && typeof config === "object") {
    pushTemplateFieldString(out, config.path ?? config.Path);
    pushTemplateFieldString(out, config.connectionString ?? config.ConnectionString);
    pushTemplateFieldString(out, config.hostName ?? config.HostName);
    pushTemplateFieldString(out, config.region ?? config.Region);
    pushTemplateFieldString(out, config.roleARN ?? config.RoleARN);
    pushTemplateFieldString(out, config.bucketName ?? config.BucketName);
    pushTemplateFieldMapValues(out, config.fileRename ?? config.FileRename);
  }

  return out;
}

export function hasTemplateVariablePlaceholders(value: string | null | undefined): boolean {
  if (!value) return false;
  return new RegExp(`\\{${TEMPLATE_VAR_INNER}\\}`).test(value);
}

export function getUndefinedTemplateVariables(
  value: string | null | undefined,
  definedVariables: string[]
): string[] {
  const used = extractTemplateVariableNames(value);
  const definedLower = new Set(definedVariables.map((v) => v.toLowerCase()));
  return used.filter((v) => !definedLower.has(v.toLowerCase()));
}

export function allTemplateVariablesDefined(value: string, definedVariables: string[]): boolean {
  return getUndefinedTemplateVariables(value, definedVariables).length === 0;
}

/**
 * Legacy [name] segments that look like old template variables (not file-rename tokens).
 * Returns unique inner names.
 */
export function findDeprecatedLegacyBracketTemplateNames(
  value: string | null | undefined
): string[] {
  if (!value) return [];
  const deprecated: string[] = [];
  const re = /\[([^\]]+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(value)) !== null) {
    const inner = m[1];
    if (!isTemplateVariableInnerName(inner)) continue;
    if (isKnownFileRenameToken(inner)) continue;
    if (!deprecated.includes(inner)) {
      deprecated.push(inner);
    }
  }
  return deprecated;
}

export function hasDeprecatedLegacyBracketTemplateSyntax(
  value: string | null | undefined
): boolean {
  return findDeprecatedLegacyBracketTemplateNames(value).length > 0;
}

/** Remove {variable} segments only (for “variable-only” / slash-stripping logic). */
export function stripTemplateVariablePlaceholders(value: string): string {
  return value.replace(new RegExp(`\\{${TEMPLATE_VAR_INNER}\\}`, "g"), "");
}

export function valueIsSingleTemplateVariablePlaceholder(value: string): boolean {
  const t = value.trim();
  return TEMPLATE_PLACEHOLDER_FULL.test(t);
}

/**
 * Built-in variable name for Amazon S3 Remote region ("Use template variable…" in the step editor).
 * Value at generate time is supplied via WorkflowTemplateMainView / generate dialog.
 */
export const BUILTIN_REGION_VARIABLE = "BUILTIN_REGION_VARIABLE";

/** Single placeholder stored in credentialPlaceholders.region for that built-in. */
export const BUILTIN_REGION_PLACEHOLDER = `{${BUILTIN_REGION_VARIABLE}}`;

export function isBuiltinRegionPlaceholderString(value: string): boolean {
  const t = (value ?? "").trim();
  return t === BUILTIN_REGION_PLACEHOLDER;
}

/** True if `name` is the built-in S3 Remote region variable (undefined-variable / Settings checks). */
export function isBuiltinRegionVariableName(name: string): boolean {
  return (name || "").trim().toLowerCase() === BUILTIN_REGION_VARIABLE.toLowerCase();
}

function stringEqualsBuiltinRegionPlaceholder(v: unknown): boolean {
  return typeof v === "string" && isBuiltinRegionPlaceholderString(v);
}

/**
 * True if a loaded template entity uses the built-in S3 Remote region placeholder in any step.
 * Accepts both list shapes returned by the API (`steps` or `workflowSteps`).
 */
export function templateEntityUsesBuiltinRegionVariable(templateData: unknown): boolean {
  const td = templateData as Record<string, unknown> | null | undefined;
  if (!td) return false;
  const steps = (td.steps ?? td.workflowSteps) as unknown[] | undefined;
  if (!Array.isArray(steps)) return false;

  for (const step of steps) {
    const s = step as Record<string, unknown>;
    const config = (s.stepConfiguration ?? s.StepConfiguration) as Record<string, unknown> | undefined;
    if (config) {
      if (stringEqualsBuiltinRegionPlaceholder(config.region) || stringEqualsBuiltinRegionPlaceholder(config.Region)) {
        return true;
      }
    }
    if (stringEqualsBuiltinRegionPlaceholder(s.region) || stringEqualsBuiltinRegionPlaceholder(s.Region)) {
      return true;
    }
    const dest = (s.destinationFileInfo ?? s.DestinationFileInfo) as Record<string, unknown> | undefined;
    if (dest) {
      if (stringEqualsBuiltinRegionPlaceholder(dest.region) || stringEqualsBuiltinRegionPlaceholder(dest.Region)) {
        return true;
      }
      const ph = (dest.credentialPlaceholders ?? dest.CredentialPlaceholders) as Record<string, unknown> | undefined;
      if (ph) {
        if (stringEqualsBuiltinRegionPlaceholder(ph.region) || stringEqualsBuiltinRegionPlaceholder(ph.Region)) {
          return true;
        }
      }
    }
  }
  return false;
}

/**
 * Synthetic variable row merged into template `variables` on API save when S3 Remote uses
 * `{BUILTIN_REGION_VARIABLE}`. Hidden from the Settings variables grid; value is collected at generate time.
 */
export function getSyntheticBuiltinRegionVariableForApi(): Pick<
  WorkflowTemplateVariable,
  "name" | "description" | "regexRule" | "ruleDescription" | "isRequired"
> {
  return {
    name: BUILTIN_REGION_VARIABLE,
    /** Shown on Generate dialog description tooltip / document icon (clipboard). */
    description:
      "AWS Region for Amazon S3 Remote step(s). When you generate a workflow, choose a region from the AWS region dropdown—the same official list used when editing workflow steps.",
    /** Shown on regex / code icon tooltip and copied as the pattern hint. */
    regexRule: "^[a-z]{2}-[a-z0-9-]+-[0-9]+$",
    /** Shown on rule tooltip / information icon (clipboard). */
    ruleDescription:
      "Must be a valid AWS Region code (for example us-east-1). Only regions returned in the application's region list are accepted at generate time.",
    isRequired: true,
  };
}

/**
 * Ensures the built-in region variable has description, ruleDescription, and regexRule for the
 * Generate Workflow dialog (tooltips and clipboard icons). Merges defaults when the API omitted
 * or cleared those fields.
 */
export function mergeBuiltinRegionVariableForGenerateUi(
  variable: WorkflowTemplateVariable
): WorkflowTemplateVariable {
  if (!isBuiltinRegionVariableName(variable.name || "")) {
    return variable;
  }
  const defaults = getSyntheticBuiltinRegionVariableForApi();
  const description = (variable.description ?? "").trim();
  const ruleDescription = (variable.ruleDescription ?? "").trim();
  const regexRule = (variable.regexRule ?? "").trim();
  return {
    ...variable,
    description: description || defaults.description!,
    ruleDescription: ruleDescription || defaults.ruleDescription!,
    regexRule: regexRule || defaults.regexRule!,
    isRequired: variable.isRequired ?? defaults.isRequired,
  };
}

/**
 * Ensures `variables[]` satisfies API validation when steps reference `{BUILTIN_REGION_VARIABLE}`.
 */
export function ensureVariablesIncludeBuiltinRegionForApi(
  variables: WorkflowTemplateVariable[],
  workflowSteps: unknown[] | undefined
): WorkflowTemplateVariable[] {
  if (!templateEntityUsesBuiltinRegionVariable({ workflowSteps })) {
    return [...variables];
  }
  const list = [...variables];
  if (list.some((v) => isBuiltinRegionVariableName(v.name || ""))) {
    return list;
  }
  list.push({ ...getSyntheticBuiltinRegionVariableForApi() });
  return list;
}

/**
 * Built-in variable name for the Business Unit selected at generate time. Always injected
 * into the Generate Workflow dialog (not stored as a step placeholder). The dialog presents a
 * dropdown sourced from the active Business Units and submits the BU id under this variable
 * name. The backend resolves the BU name from the id.
 */
export const BUILTIN_BUSUNIT_VARIABLE = "BUILTIN_BUSUNIT_VARIABLE";

/** Placeholder form for parity with the region built-in (kept for symmetry; not used in steps today). */
export const BUILTIN_BUSUNIT_PLACEHOLDER = `{${BUILTIN_BUSUNIT_VARIABLE}}`;

export function isBuiltinBusUnitPlaceholderString(value: string): boolean {
  const t = (value ?? "").trim();
  return t === BUILTIN_BUSUNIT_PLACEHOLDER;
}

/** True if `name` is the built-in Business Unit variable (Settings checks, dialog branching). */
export function isBuiltinBusUnitVariableName(name: string): boolean {
  return (name || "").trim().toLowerCase() === BUILTIN_BUSUNIT_VARIABLE.toLowerCase();
}

/**
 * True when a workflow-template step should use the locked Pull SMB bucket name
 * (`{env}-smb-{BUILTIN_BUSUNIT_VARIABLE}`) — Pull + File Download/Delete + Amazon S3.
 */
export function isTemplatePullSmbBucketStep(
  transferType: string | null | undefined,
  functionName: string | null | undefined,
  storageType: string | null | undefined
): boolean {
  const fn = (functionName ?? "").trim().toLowerCase();
  const isDownloadOrDelete = fn === "filedownload" || fn === "filedelete";
  const isPull = (transferType ?? "").trim().toLowerCase() === "pull";
  const isS3 = (storageType ?? "").trim().toLowerCase() === "amazons3";
  return isDownloadOrDelete && isPull && isS3;
}

/**
 * Bucket name shape stored in Dynamo for template Pull SMB steps. The UI shows
 * `{env}-smb-{BUILTIN_BUSUNIT_VARIABLE}` in the step editor only; persistence and API
 * stamping use the env SMB prefix with a trailing hyphen (e.g. `efm-dev-smb-`). The API
 * completes the suffix from the template or generate-time Business Unit.
 */
export function toPersistedTemplatePullSmbBucketName(
  transferType: string | null | undefined,
  functionName: string | null | undefined,
  storageType: string | null | undefined,
  currentName: string | null | undefined
): string {
  if (!isTemplatePullSmbBucketStep(transferType, functionName, storageType)) {
    return (currentName ?? "").toString();
  }
  const smb = GlobalVariables.SMB_BUCKET_NAME;
  if (!smb || smb === "undefined") {
    return (currentName ?? "").toString();
  }
  return `${smb}-`;
}

/**
 * True if the template category should be treated as Global. Accepts the canonical lowercase
 * `"global"` plus legacy literal categories `"all"` and `"default"` (case-insensitive). Used to
 * branch Generate dialog rendering for the BUSUNIT built-in.
 */
export function isGlobalTemplateCategory(category: string | null | undefined): boolean {
  const c = (category ?? "").trim().toLowerCase();
  return c === "global" || c === "all" || c === "default";
}

/**
 * Synthetic variable row injected into the Generate Workflow dialog so the BU dropdown always
 * renders. Hidden from the Settings variables grid; value is collected at generate time.
 */
export function getSyntheticBuiltinBusUnitVariableForApi(): Pick<
  WorkflowTemplateVariable,
  "name" | "description" | "regexRule" | "ruleDescription" | "isRequired"
> {
  return {
    name: BUILTIN_BUSUNIT_VARIABLE,
    /** Shown on Generate dialog description tooltip / document icon (clipboard). */
    description:
      "Business Unit for the generated workflow. When you generate a workflow, choose a Business Unit from the dropdown. The list contains active Business Units; for non-Global templates the Business Unit is fixed to the template's Business Unit.",
    /** Empty: Business Unit values come from the API list, not a regex. */
    regexRule: "",
    /** Shown on rule tooltip / information icon (clipboard). */
    ruleDescription:
      "Must be a Business Unit returned by the application's Business Unit list. The legacy values 'ALL' and 'DEFAULT' are also accepted on Global templates.",
    isRequired: true,
  };
}

/**
 * Ensures the built-in Business Unit variable carries description / ruleDescription text for the
 * Generate Workflow dialog tooltips and clipboard icons. Merges defaults when the API omitted or
 * cleared those fields. Unlike region, regex stays empty (whitelist-validated).
 */
export function mergeBuiltinBusUnitVariableForGenerateUi(
  variable: WorkflowTemplateVariable
): WorkflowTemplateVariable {
  if (!isBuiltinBusUnitVariableName(variable.name || "")) {
    return variable;
  }
  const defaults = getSyntheticBuiltinBusUnitVariableForApi();
  const description = (variable.description ?? "").trim();
  const ruleDescription = (variable.ruleDescription ?? "").trim();
  return {
    ...variable,
    description: description || defaults.description!,
    ruleDescription: ruleDescription || defaults.ruleDescription!,
    regexRule: variable.regexRule ?? defaults.regexRule!,
    isRequired: variable.isRequired ?? defaults.isRequired,
  };
}

/**
 * Template credential fields must not contain literal secrets—only `{variable}` placeholders
 * (same rule as AddWorkflowStepPopup in template mode). Empty values are allowed here;
 * required-field checks are separate.
 */
export function isTemplateCredentialFieldVariableOnly(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return true;
  const trimmed = String(value).trim();
  if (!trimmed.length) return true;
  const withoutVariables = stripTemplateVariablePlaceholders(trimmed);
  if (!withoutVariables.length) return true;
  return /^[\.\-_]*$/.test(withoutVariables);
}

function readCredentialMapEntry(
  cred: Record<string, unknown> | null | undefined,
  camelKey: string
): string {
  if (!cred || typeof cred !== "object") return "";
  const pascalKey = camelKey.charAt(0).toUpperCase() + camelKey.slice(1);
  const v = (cred as Record<string, unknown>)[camelKey] ?? (cred as Record<string, unknown>)[pascalKey];
  return v !== undefined && v !== null ? String(v) : "";
}

export type WorkflowTemplateCredentialLiteralIssue = {
  /** 1-based step number in saved order */
  stepNumber: number;
  stepName: string;
  fieldLabel: string;
  /** Short preview of the offending value */
  preview: string;
};

/**
 * Scan workflow steps as built for save (destinationFileInfo.credentialPlaceholders / secretValue)
 * and report credential strings that are not variable-only. Used for save-time UX before/around API errors.
 */
export function findWorkflowTemplateStepCredentialLiterals(
  workflowSteps: unknown[] | null | undefined
): WorkflowTemplateCredentialLiteralIssue[] {
  const issues: WorkflowTemplateCredentialLiteralIssue[] = [];
  if (!Array.isArray(workflowSteps)) return issues;

  workflowSteps.forEach((step: any, idx: number) => {
    const dest = step?.destinationFileInfo || step?.DestinationFileInfo || {};
    const ph = (dest.credentialPlaceholders || dest.CredentialPlaceholders || {}) as Record<string, unknown>;
    const sv = (dest.secretValue || dest.SecretValue || {}) as Record<string, unknown>;
    const cred = { ...sv, ...ph };
    const storage = String(dest.storageType || dest.StorageType || "").toLowerCase();
    const fn = String(step?.functionName || step?.FunctionName || "").toLowerCase();

    const stepName = String(step?.stepName || step?.StepName || "").trim() || "(unnamed step)";
    const stepNumber = idx + 1;

    const pushIfBad = (fieldLabel: string, value: string) => {
      const t = value.trim();
      if (!t) return;
      if (!isTemplateCredentialFieldVariableOnly(value)) {
        const preview = t.length > 48 ? `${t.slice(0, 45)}…` : t;
        issues.push({ stepNumber, stepName, fieldLabel, preview });
      }
    };

    if (fn === "fileencrypt") {
      pushIfBad("PGP public key", readCredentialMapEntry(cred, "pgpKey"));
    }

    if (storage === "sftp" || storage === "ftps") {
      pushIfBad("Host", readCredentialMapEntry(cred, "host"));
      pushIfBad("Port", readCredentialMapEntry(cred, "port"));
      pushIfBad("User name", readCredentialMapEntry(cred, "userName"));
      pushIfBad("Password", readCredentialMapEntry(cred, "password"));
    }
    if (storage === "azure") {
      pushIfBad("Connection string", readCredentialMapEntry(cred, "connectionString"));
    }
    if (storage === "amazons3remote") {
      pushIfBad("Role ARN", readCredentialMapEntry(cred, "roleARN"));
      pushIfBad("Region", readCredentialMapEntry(cred, "region"));
    }
  });

  return issues;
}
